import { useQuery } from "@tanstack/react-query";

/** This tab's commit, compiled in by CI; unset in dev and local builds, which turns checking off. */
export const RUNNING_BUILD: string | undefined =
  import.meta.env.VITE_APP_VERSION || undefined;

// Written by `buildVersion` in vite.config.ts; frontend-deploy.yml uploads it last, uncached.
const VERSION_PATH = "/version.json";

const fetchDeployedBuild = async (signal: AbortSignal): Promise<string> => {
  const response = await fetch(VERSION_PATH, { cache: "no-store", signal });
  if (!response.ok) throw new Error(`${VERSION_PATH}: ${response.status}`);
  // CloudFront answers a missing file with index.html, so this can throw too.
  const body = (await response.json()) as { version?: unknown } | null;
  if (typeof body?.version !== "string" || !body.version)
    throw new Error(`${VERSION_PATH}: no version`);
  return body.version;
};

/**
 * The deployed build when it differs from this tab's, checked every `pollMs`
 * (`null` stops this caller's polling). Hidden tabs poll only with `inBackground`.
 */
export const useNewBuild = (pollMs: number | null, inBackground = false) => {
  const {
    data: deployed,
    dataUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: ["deployed-build"],
    queryFn: ({ signal }) => fetchDeployedBuild(signal),
    enabled: !!RUNNING_BUILD && pollMs !== null,
    staleTime: pollMs ?? Infinity,
    refetchInterval: pollMs ?? false,
    refetchIntervalInBackground: inBackground,
    retry: false,
  });
  return {
    newBuild: deployed !== RUNNING_BUILD ? deployed : undefined,
    checkedAt: dataUpdatedAt,
    refetch,
  };
};
